import { Business, PersonSearch } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Card,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from "@mui/material";
import moment from "moment";
import qs from "query-string";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Loader from "../../../../components/Loader/Loader";
import { getUserData } from "../../../../utils/FunctionHelpers";
import { searchCandidats } from "../../../Services/CandidatService";
import { getAll as getAllContacts } from "../../../Services/ContactService";
import "./recentActivity.css";

moment.locale("fr");

const STATUS_STYLES = {
  "client actif": { color: "#0ca30c", background: "rgba(12, 163, 12, 0.12)" },
  actif: { color: "#0ca30c", background: "rgba(12, 163, 12, 0.12)" },
  qualifié: { color: "#0ca30c", background: "rgba(12, 163, 12, 0.12)" },
  "en attente": { color: "#fab219", background: "rgba(250, 178, 25, 0.16)" },
  prospect: { color: "#4f46e5", background: "rgba(79, 70, 229, 0.12)" },
  nouveau: { color: "#4f46e5", background: "rgba(79, 70, 229, 0.12)" },
  "en cours": { color: "#fb8b24", background: "rgba(251, 139, 36, 0.14)" },
};

const AVATAR_COLORS = ["#4f46e5", "#fb8b24", "#14b8a6", "#f5b700", "#ec4899"];

const hashColor = (name) => {
  const sum = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
};

const StatusChip = ({ label }) => {
  const style = STATUS_STYLES[label.toLowerCase()] || { color: "#52514e", background: "rgba(82, 81, 78, 0.1)" };
  return (
    <Chip
      label={label.toUpperCase()}
      size="small"
      className="recentActivityStatusBadge"
      sx={{ color: style.color, backgroundColor: style.background }}
    />
  );
};

const getInitials = (name) =>
  name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const ActivityCard = ({ icon, title, viewAllTo, items, loading, emptyLabel }) => (
  <Card className="recentActivityCard" elevation={0}>
    <Box className="recentActivityHeader">
      <Box className="recentActivityHeaderLeft">
        {icon}
        <Typography component="strong" className="recentActivityHeaderTitle">
          {title}
        </Typography>
      </Box>
      <Link to={viewAllTo} className="recentActivityViewAll">
        Voir tout →
      </Link>
    </Box>
    {loading ? (
      <Loader />
    ) : items.length === 0 ? (
      <Typography className="recentActivityEmpty">{emptyLabel}</Typography>
    ) : (
      <List>
        {items.map((item) => (
          <ListItem key={item.uuid} divider component={Link} to={item.link} className="recentActivityItem">
            <ListItemAvatar>
              <Avatar className="recentActivityAvatar" sx={{ bgcolor: hashColor(item.name || "?") }}>
                {getInitials(item.name)}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={<span className="recentActivityItemName">{item.name || "—"}</span>}
              secondary={
                <span className="recentActivityItemSubtitle">
                  {[item.subtitle, item.date ? moment(item.date).fromNow() : null].filter(Boolean).join(" · ")}
                </span>
              }
            />
            {item.status && <StatusChip label={item.status} />}
          </ListItem>
        ))}
      </List>
    )}
  </Card>
);

const RecentActivity = () => {
  const [candidats, setCandidats] = useState([]);
  const [loadingCandidats, setLoadingCandidats] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const userUuid = getUserData()?.uuid;

  useEffect(() => {
    const query = qs.stringify({ sourceur: userUuid ,motsCleCategorie: ""});
    searchCandidats(query, 1, 3)
      .then(({ data }) => {
        const sorted = [...(data?.candidats || [])].sort(
          (a, b) => new Date(b.updatedAt?.date) - new Date(a.updatedAt?.date)
        );
        setCandidats(
          sorted.slice(0, 3).map((c) => ({
            uuid: c.id,
            name: `${c.prenom || ""} ${c.nom || ""}`.trim(),
            subtitle: c.fonction,
            date: c.updatedAt?.date,
            link: `/candidats/${c.id}/view`,
          }))
        );
      })
      .catch(() => setCandidats([]))
      .finally(() => setLoadingCandidats(false));
  }, []);

  useEffect(() => {
    getAllContacts(1,3)
      .then(({ data }) => {
        const sorted = [...(data?.contacts || [])].sort(
          (a, b) => new Date(b.createdAt?.date) - new Date(a.createdAt?.date)
        );
        setContacts(
          sorted.slice(0, 3).map((c) => ({
            uuid: c.uuid,
            name: `${c.prenom || ""} ${c.nom || ""}`.trim(),
            subtitle: c.societe,
            status: c.statut,
            date: c.createdAt?.date,
            link: `/societe/${c.societeId}/contacts/${c.uuid}/view`,
          }))
        );
      })
      .catch(() => setContacts([]))
      .finally(() => setLoadingContacts(false));
  }, []);

  return (
    <>
      <ActivityCard
        icon={<PersonSearch className="recentActivityHeaderIcon" />}
        title="DERNIERS CANDIDATS IMPORTÉS"
        viewAllTo={`/accueil/chercher?categorie=candidat&sourceur=${userUuid || ""}`}
        items={candidats}
        loading={loadingCandidats}
        emptyLabel="Aucun candidat récent."
      />
      <ActivityCard
        icon={<Business className="recentActivityHeaderIcon" />}
        title="DERNIERS CONTACTS AJOUTÉS"
        viewAllTo="/accueil/chercher?categorie=contact"
        items={contacts}
        loading={loadingContacts}
        emptyLabel="Aucun contact récent."
      />
    </>
  );
};

export default RecentActivity;
